import React, { useEffect, useState, useCallback } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Select,
  Space,
  message,
  Divider,
  Drawer,
  Descriptions,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  LockOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { purchases, products as prodApi, suppliers as suppApi } from "../api";
import PageHeader from "../components/common/PageHeader";
import { fmtCurrency, fmtDate } from "../utils/format";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;
const SECRET_CODE = "admin123";

export default function Purchases() {
  const [data, setData] = useState({ content: [], totalElements: 0 });
  const [loading, setLoading] = useState(false);
  const [allProds, setAllProds] = useState([]);
  const [allSupps, setAllSupps] = useState([]);
  const [page, setPage] = useState(0);

  // ── Filters ───────────────────────────────────────────────────
  const [q, setQ] = useState("");
  const [dateRange, setDateRange] = useState([]);

  // Create modal
  const [createModal, setCreateModal] = useState(false);
  const [createLines, setCreateLines] = useState([
    { productId: null, quantity: 1, rate: 0 },
  ]);
  const [createForm] = Form.useForm();

  // View drawer
  const [viewing, setViewing] = useState(null);

  // Secret code gate — used for both edit and delete
  const [secretModal, setSecretModal] = useState(false);
  const [secretInput, setSecretInput] = useState("");
  const [secretAction, setSecretAction] = useState(null); // { type: 'edit'|'delete', record }
  const [secretError, setSecretError] = useState("");

  // Edit modal
  const [editModal, setEditModal] = useState(false);
  const [editLines, setEditLines] = useState([]);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm] = Form.useForm();

  const load = useCallback(async (pg = page, search = q, range = dateRange) => {
    setLoading(true);
    try {
      const r = await purchases.list({
        page: pg, size: 20,
        q: search || undefined,
        fromDate: range?.length === 2 ? range[0].format("YYYY-MM-DD") : undefined,
        toDate:   range?.length === 2 ? range[1].format("YYYY-MM-DD") : undefined,
      });
      setData(r.data.data);
    } finally {
      setLoading(false);
    }
  }, [page, q, dateRange]);

  useEffect(() => { load(); }, [page]);

  const handleSearch = (val) => { setQ(val); setPage(0); load(0, val, dateRange); };
  const handleDateRange = (range) => { setDateRange(range || []); setPage(0); load(0, q, range || []); };

  useEffect(() => {
    prodApi.all().then((r) => setAllProds(r.data.data));
    suppApi.list().then((r) => setAllSupps(r.data.data));
  }, []);

  // ── Secret code gate ──────────────────────────────────────────
  const requestSecret = (type, record) => {
    setSecretAction({ type, record });
    setSecretInput("");
    setSecretError("");
    setSecretModal(true);
  };

  const confirmSecret = () => {
    if (secretInput !== SECRET_CODE) {
      setSecretError("Incorrect code. Please try again.");
      return;
    }
    setSecretModal(false);
    if (secretAction.type === "edit") openEdit(secretAction.record);
    if (secretAction.type === "delete") doDelete(secretAction.record.id);
  };

  // ── View ──────────────────────────────────────────────────────
  const openView = async (rec) => {
    const r = await purchases.get(rec.id);
    setViewing(r.data.data);
  };

  // ── Edit ──────────────────────────────────────────────────────
  const openEdit = async (rec) => {
    const r = await purchases.get(rec.id);
    const full = r.data.data;
    setEditTarget(full);
    editForm.setFieldsValue({
      date: dayjs(full.date),
      supplierId: full.supplier?.id,
      notes: full.notes,
    });
    setEditLines(
      full.items.map((i) => ({
        productId: i.product.id,
        quantity: Number(i.quantity),
        rate: Number(i.rate),
      })),
    );
    setEditModal(true);
  };

  const onEditSave = async () => {
    try {
      const vals = await editForm.validateFields();
      if (editLines.some((l) => !l.productId)) {
        message.warning("Please select a product for all items");
        return;
      }
      await purchases.update(editTarget.id, {
        ...vals,
        date: vals.date.format("YYYY-MM-DD"),
        items: editLines,
      });
      message.success("Purchase updated");
      setEditModal(false);
      load();
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to update purchase";
      message.error(msg);
    }
  };

  // ── Delete ────────────────────────────────────────────────────
  const doDelete = async (id) => {
    try {
      await purchases.delete(id);
      message.success("Purchase deleted");
      load();
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to delete purchase";
      message.error(msg);
    }
  };

  // ── Create ────────────────────────────────────────────────────
  const openCreate = () => {
    createForm.resetFields();
    createForm.setFieldsValue({ date: dayjs() });
    setCreateLines([{ productId: null, quantity: 1, rate: 0 }]);
    setCreateModal(true);
  };

  const onCreateSave = async () => {
    try {
      const vals = await createForm.validateFields();
      if (createLines.some((l) => !l.productId)) {
        message.warning("Please select a product for all items");
        return;
      }
      if (createLines.some((l) => !l.quantity || l.quantity <= 0)) {
        message.warning("Quantity must be greater than 0 for all items");
        return;
      }
      await purchases.create({
        ...vals,
        date: vals.date.format("YYYY-MM-DD"),
        items: createLines,
      });
      message.success("Purchase saved");
      setCreateModal(false);
      load();
    } catch {
      message.error("Failed to save purchase");
    }
  };

  // ── Line helpers ──────────────────────────────────────────────
  const addLine = (lines, setLines) =>
    setLines([...lines, { productId: null, quantity: 1, rate: 0 }]);
  const removeLine = (lines, setLines, i) =>
    setLines(lines.filter((_, idx) => idx !== i));
  const updateLine = (lines, setLines, i, field, val) => {
    const l = [...lines];
    l[i][field] = val;
    setLines(l);
  };
  const lineTotal = (lines) =>
    lines.reduce((s, l) => s + (l.quantity || 0) * (l.rate || 0), 0);

  const renderLines = (lines, setLines) => (
    <>
      {lines.map((l, i) => (
        <Space key={i} style={{ width: "100%", marginBottom: 8 }} align="start">
          <Select
            placeholder="Product"
            style={{ width: 220 }}
            value={l.productId}
            onChange={(v) => updateLine(lines, setLines, i, "productId", v)}
            options={allProds.map((p) => ({ value: p.id, label: p.name }))}
            showSearch
            filterOption={(input, opt) =>
              opt.label.toLowerCase().includes(input.toLowerCase())
            }
          />
          <InputNumber
            placeholder="Qty"
            min={0.001}
            value={l.quantity}
            step={0.5}
            onChange={(v) => updateLine(lines, setLines, i, "quantity", v)}
            style={{ width: 90 }}
          />
          <InputNumber
            placeholder="Rate ₹"
            min={0}
            value={l.rate}
            onChange={(v) => updateLine(lines, setLines, i, "rate", v)}
            style={{ width: 110 }}
            prefix="₹"
          />
          <span
            className="mono"
            style={{
              lineHeight: "32px",
              fontSize: 12,
              color: "var(--text-muted)",
            }}
          >
            = {fmtCurrency((l.quantity || 0) * (l.rate || 0))}
          </span>
          {lines.length > 1 && (
            <Button
              icon={<DeleteOutlined />}
              size="small"
              danger
              onClick={() => removeLine(lines, setLines, i)}
            />
          )}
        </Space>
      ))}
      <Button
        icon={<PlusOutlined />}
        size="small"
        onClick={() => addLine(lines, setLines)}
        style={{ marginTop: 4 }}
      >
        Add Item
      </Button>
      <Divider style={{ borderColor: "var(--border)" }} />
      <div
        style={{
          textAlign: "right",
          fontFamily: "var(--font-mono)",
          fontSize: 16,
          color: "var(--text)",
        }}
      >
        Total: <strong>{fmtCurrency(lineTotal(lines))}</strong>
      </div>
    </>
  );

  const cols = [
    {
      title: "Purchase No",
      dataIndex: "purchaseNo",
      render: (v) => <span className="mono text-info">{v}</span>,
    },
    { title: "Date", dataIndex: "date", render: fmtDate },
    {
      title: "Supplier",
      dataIndex: ["supplier", "name"],
      render: (v) => v || <span style={{ color: "var(--text-muted)" }}>—</span>,
    },
    {
      title: "Total",
      dataIndex: "total",
      render: (v) => <span className="mono">{fmtCurrency(v)}</span>,
    },
    {
      title: "Actions",
      key: "actions",
      width: 220,
      render: (_, rec) => (
        <Space>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => openView(rec)}
          >
            View
          </Button>
          <Button
            size="small"
            icon={<LockOutlined />}
            onClick={() => requestSecret("edit", rec)}
          >
            Edit
          </Button>
          <Button
            size="small"
            icon={<LockOutlined />}
            danger
            onClick={() => requestSecret("delete", rec)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Purchases"
        subtitle="Stock incoming from suppliers"
        actions={[
          <Button
            key="a"
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreate}
          >
            New Purchase
          </Button>,
        ]}
      />

      {/* ── Filters ────────────────────────────── */}
      <Space wrap style={{ marginBottom: 16 }}>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search purchase no, supplier, product..."
          value={q}
          onChange={(e) => handleSearch(e.target.value)}
          allowClear
          style={{ width: 300 }}
        />
        <RangePicker
          value={dateRange}
          onChange={handleDateRange}
          style={{ width: 260 }}
        />
      </Space>

      <Table
        scroll={{ x: "max-content" }}
        columns={cols}
        dataSource={data.content}
        rowKey="id"
        loading={loading}
        size="small"
        pagination={{
          total: data.totalElements,
          pageSize: 20,
          current: page + 1,
          onChange: (p) => setPage(p - 1),
        }}
      />

      {/* ── Secret Code Modal ── */}
      <Modal
        open={secretModal}
        title={
          <Space>
            <LockOutlined />
            Enter Secret Code to{" "}
            {secretAction?.type === "edit" ? "Edit" : "Delete"}
          </Space>
        }
        onCancel={() => setSecretModal(false)}
        onOk={confirmSecret}
        okText="Confirm"
        okButtonProps={{ danger: secretAction?.type === "delete" }}
      >
        <p style={{ color: "var(--text-muted)", marginBottom: 12 }}>
          This action is protected. Enter the secret code to proceed.
        </p>
        <Input.Password
          placeholder="Secret code"
          value={secretInput}
          onChange={(e) => {
            setSecretInput(e.target.value);
            setSecretError("");
          }}
          onPressEnter={confirmSecret}
          autoFocus
        />
        {secretError && (
          <p style={{ color: "var(--danger)", marginTop: 8, fontSize: 12 }}>
            {secretError}
          </p>
        )}
      </Modal>

      {/* ── View Drawer ── */}
      <Drawer
        open={!!viewing}
        title="Purchase Details"
        onClose={() => setViewing(null)}
        width={520}
        extra={
          <Button
            icon={<LockOutlined />}
            onClick={() => {
              setViewing(null);
              requestSecret("edit", viewing);
            }}
          >
            Edit
          </Button>
        }
      >
        {viewing && (
          <>
            <Descriptions
              column={1}
              bordered
              size="small"
              style={{ marginBottom: 16 }}
            >
              <Descriptions.Item label="Purchase No">
                <span className="mono">{viewing.purchaseNo}</span>
              </Descriptions.Item>
              <Descriptions.Item label="Date">
                {fmtDate(viewing.date)}
              </Descriptions.Item>
              <Descriptions.Item label="Supplier">
                {viewing.supplier?.name || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Notes">
                {viewing.notes || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Total">
                <strong>{fmtCurrency(viewing.total)}</strong>
              </Descriptions.Item>
            </Descriptions>
            <Divider
              style={{
                borderColor: "var(--border)",
                color: "var(--success,#52c41a)",
              }}
            >
              Items
            </Divider>
            <Table
              size="small"
              pagination={{ pageSize: 10, showSizeChanger: false, size: "small" }}
              rowKey="id"
              dataSource={viewing.items || []}
              columns={[
                { title: "Product", dataIndex: ["product", "name"] },
                {
                  title: "Qty",
                  dataIndex: "quantity",
                  render: (v) => <span className="mono">{v}</span>,
                },
                {
                  title: "Rate",
                  dataIndex: "rate",
                  render: (v) => <span className="mono">{fmtCurrency(v)}</span>,
                },
                {
                  title: "Amount",
                  dataIndex: "amount",
                  render: (v) => <span className="mono">{fmtCurrency(v)}</span>,
                },
              ]}
            />
          </>
        )}
      </Drawer>

      {/* ── Edit Modal ── */}
      <Modal
        open={editModal}
        title="Edit Purchase"
        onCancel={() => setEditModal(false)}
        onOk={onEditSave}
        width={700}
        okText="Update Purchase"
      >
        <Form form={editForm} layout="vertical">
          <Space>
            <Form.Item name="date" label="Date" rules={[{ required: true }]}>
              <DatePicker />
            </Form.Item>
            <Form.Item name="supplierId" label="Supplier">
              <Select
                placeholder="Select supplier"
                style={{ width: 200 }}
                options={allSupps.map((s) => ({ value: s.id, label: s.name }))}
              />
            </Form.Item>
          </Space>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} placeholder="Optional notes..." />
          </Form.Item>
        </Form>
        <Divider style={{ borderColor: "var(--border)" }}>Items</Divider>
        {renderLines(editLines, setEditLines)}
      </Modal>

      {/* ── Create Modal ── */}
      <Modal
        open={createModal}
        title="New Purchase Entry"
        onCancel={() => setCreateModal(false)}
        onOk={onCreateSave}
        width={700}
        okText="Save Purchase"
      >
        <Form form={createForm} layout="vertical">
          <Space>
            <Form.Item name="date" label="Date" rules={[{ required: true }]}>
              <DatePicker />
            </Form.Item>
            <Form.Item name="supplierId" label="Supplier">
              <Select
                placeholder="Select supplier"
                style={{ width: 200 }}
                options={allSupps.map((s) => ({ value: s.id, label: s.name }))}
              />
            </Form.Item>
          </Space>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} placeholder="Optional notes..." />
          </Form.Item>
        </Form>
        <Divider style={{ borderColor: "var(--border)" }}>Items</Divider>
        {renderLines(createLines, setCreateLines)}
      </Modal>
    </div>
  );
}